import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
    MapPin, ChevronLeft, ChevronRight, Star, 
    Calendar, Clock, Filter, Search
} from 'lucide-react';
import { Input } from '../components/ui/input';

const categories = [
    { id: 'all', label: 'All' },
    { id: 'safari', label: 'Safari' },
    { id: 'mountain', label: 'Mountain' },
    { id: 'beach', label: 'Beach' },
    { id: 'cultural', label: 'Cultural' },
    { id: 'wildlife', label: 'Wildlife' }
];

export default function Destinations() {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const { api } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDestinations();
    }, [activeCategory]);

    const fetchDestinations = async () => {
        try {
            const params = activeCategory !== 'all' ? `?category=${activeCategory}` : '';
            const response = await api.get(`/destinations${params}`);
            setDestinations(response.data);
        } catch (error) {
            console.error('Failed to fetch destinations');
        } finally {
            setLoading(false);
        }
    };

    const filteredDestinations = destinations.filter(dest => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return true;
        return dest.name.toLowerCase().includes(query) ||
               dest.description.toLowerCase().includes(query) ||
               dest.category?.toLowerCase().includes(query) ||
               dest.region?.toLowerCase().includes(query);
    });

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="text-xl font-bold text-[#073763]">
                            safarinodes
                        </Link>
                        <Button 
                            onClick={() => navigate('/trip/create')}
                            className="btn-primary"
                            data-testid="header-start-trip-btn"
                        >
                            Start Planning
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="bg-[#073763] py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-white/60 font-medium mb-3 uppercase tracking-wide text-sm">Discover</p>
                    <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
                        Tanzanian Destinations
                    </h1>
                    <p className="text-white/70 max-w-2xl mx-auto mb-8">
                        From the endless plains of the Serengeti to the pristine beaches of Zanzibar, explore the wonders of Tanzania.
                    </p>
                    
                    {/* Search */}
                    <div className="max-w-md mx-auto relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <Input
                            type="text"
                            placeholder="Search destinations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-12 py-6 rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20"
                            data-testid="search-destinations"
                        />
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="border-b border-gray-200 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-2 overflow-x-auto py-4 scrollbar-hide">
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveCategory(cat.id)}
                                className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                                    activeCategory === cat.id
                                        ? 'bg-[#073763] text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-[#073763]/10'
                                }`}
                                data-testid={`category-${cat.id}`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Destinations Grid */}
            <section className="py-12 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <div className="spinner" />
                        </div>
                    ) : filteredDestinations.length === 0 ? (
                        <div className="text-center py-16">
                            <MapPin className="mx-auto mb-4 text-gray-300" size={48} />
                            <h3 className="font-heading text-xl font-semibold mb-2">No destinations found</h3>
                            <p className="text-[#4A4A4A]">Try adjusting your search or filter</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredDestinations.map(destination => (
                                <div 
                                    key={destination.id}
                                    className="card-marketing group cursor-pointer"
                                    onClick={() => navigate('/trip/create')}
                                    data-testid={`destination-card-${destination.id}`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img 
                                            src={destination.image_url}
                                            alt={destination.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-[#073763] uppercase tracking-wider">
                                                {destination.category}
                                            </span>
                                            <span className="text-xs text-gray-500">{destination.region}</span>
                                        </div>
                                        <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
                                            {destination.name}
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                                            {destination.description}
                                        </p>
                                        
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {destination.highlights?.slice(0, 3).map((highlight, idx) => (
                                                <span 
                                                    key={idx}
                                                    className="text-xs bg-[#073763]/10 px-2 py-1 rounded text-[#073763]"
                                                >
                                                    {highlight}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                            <div className="flex items-center gap-1 text-sm text-gray-500">
                                                <Calendar size={14} />
                                                {destination.best_time}
                                            </div>
                                            <span className="text-[#073763] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                                Plan Trip <ChevronRight size={16} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-[#073763]">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        Ready to explore Tanzania?
                    </h2>
                    <p className="text-white/70 mb-8">
                        Create your trip snapshot and receive personalized itineraries from local experts.
                    </p>
                    <Button 
                        onClick={() => navigate('/trip/create')}
                        className="bg-white text-[#073763] hover:bg-gray-100 px-8 py-6 rounded-full font-semibold"
                        data-testid="cta-start-planning-btn"
                    >
                        Start Planning Your Trip
                        <ChevronRight className="ml-2" size={18} />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t border-gray-100 py-8">
                <div className="max-w-7xl mx-auto px-6 text-center text-sm text-[#757575]">
                    <p>© 2026 safarinodes. Travel Fair, Travel Local.</p>
                </div>
            </footer>
        </div>
    );
}
